// src/AdminChatInterface.js
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import Picker from '@emoji-mart/react';
import ChatList from './ChatList';

const AdminChatInterface = () => {
    const { isAdmin } = useAuth();

    // Instead of null, initialize selectedRoom to an empty string 
    // to avoid prop-type warnings in ChatList.
    const [selectedRoom, setSelectedRoom] = useState('');
    const [chats, setChats] = useState([]);
    const [messages, setMessages] = useState([]);
    const [response, setResponse] = useState('');
    const [socket, setSocket] = useState(null);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);

    // Keep track of visitor message IDs to avoid duplicates.
    const [messageIds] = useState(new Set());
    const messagesEndRef = useRef(null);
    const emojiPickerRef = useRef(null);

    // Scroll to bottom when messages update
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Helper: deduplicate messages by (timestamp + message)
    const deduplicateMessages = (msgs) => {
        const seen = new Set();
        return msgs.filter((msg) => {
            const key = `${msg.timestamp}-${msg.message}`;
            if (seen.has(key)) {
                return false;
            } else {
                seen.add(key);
                return true;
            }
        });
    };

    // Mark messages as read on the server
    const markMessagesAsRead = async (roomId) => {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/api/chat/messages/mark-read/`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                    },
                    body: JSON.stringify({ room_id: roomId }),
                }
            );

            if (!res.ok) {
                const errorText = await res.text();
                console.error('Error marking messages as read:', res.status, errorText);
                return;
            }

            // If successful, update local messages to mark them as read
            setMessages((prev) => prev.map((msg) => ({ ...msg, is_read: true })));
            // Refresh chat list to update unread counts
            fetchChats();
        } catch (error) {
            console.error('Error marking messages as read:', error);
        }
    };

    // Fetch chat rooms (unique room IDs)
    const fetchChats = async () => {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/api/chat/messages/`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                    },
                }
            );

            if (!res.ok) {
                const errorText = await res.text();
                console.error('Error fetching chats:', res.status, errorText);
                return;
            }

            const data = await res.json();
            const uniqueRooms = [...new Set(data.map((msg) => msg.room_id))];
            setChats(uniqueRooms);
        } catch (error) {
            console.error('Error fetching chats:', error);
        }
    };

    // Fetch initial chats if admin
    useEffect(() => {
        if (!isAdmin) return;
        fetchChats();

        // Poll for new chats every 10s (optional)
        const interval = setInterval(fetchChats, 10000);
        return () => clearInterval(interval);
    }, [isAdmin]);

    // Connect WebSocket when a room is selected
    useEffect(() => {
        // If no room selected, skip connecting
        if (!selectedRoom) return;

        // Build the WebSocket URL dynamically.
        // Replace "http" with "ws" so if REACT_APP_API_URL starts with "https", it becomes "wss"
        const wsUrl = `${process.env.REACT_APP_API_URL.replace(/^http/, 'ws')}/ws/chat/${selectedRoom}/`;
        const ws = new WebSocket(wsUrl);
        setSocket(ws);

        ws.onopen = () => {
            console.log(`✅ WebSocket Connected to room: ${selectedRoom}`);
        };

        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                console.log('📩 Received Message:', data);

                // Deduplicate admin messages
                if (data.sender === 'Admin') {
                    // Check if a similar admin message already exists
                    const exists = messages.some(
                        (msg) =>
                            msg.sender === 'Admin' &&
                            msg.message === data.message &&
                            Math.abs(new Date(data.timestamp) - new Date(msg.timestamp)) < 1000
                    );
                    if (exists) return;
                } else {
                    // For visitor messages, check the messageIds set
                    const messageId = `${data.timestamp}-${data.message}`;
                    if (messageIds.has(messageId)) return;
                    messageIds.add(messageId);
                }

                // Add new message & sort by timestamp
                setMessages((prev) => {
                    const updated = deduplicateMessages([...prev, data]);
                    return updated.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
                });

                // If the message is from a Visitor, mark it as read
                if (data.sender === 'Visitor') {
                    markMessagesAsRead(selectedRoom);
                }
            } catch (err) {
                console.error('❌ Error parsing WebSocket message:', err);
            }
        };

        ws.onerror = (error) => {
            console.error('❌ WebSocket Error:', error);
        };

        ws.onclose = () => {
            console.log('🔌 WebSocket Disconnected.');
        };

        // Cleanup on unmount or when selectedRoom changes
        return () => {
            ws.close();
        };
    }, [selectedRoom, messageIds, messages]);

    // Fetch messages when a room is selected
    const handleRoomSelect = async (roomId) => {
        setSelectedRoom(roomId);

        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/api/chat/messages/?room_id=${roomId}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                    },
                }
            );

            if (!res.ok) {
                const errorText = await res.text();
                console.error('Error fetching messages for room:', res.status, errorText);
                return;
            }

            const data = await res.json();
            const uniqueMessages = deduplicateMessages(data);
            uniqueMessages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
            setMessages(uniqueMessages);

            // Mark them as read immediately
            await markMessagesAsRead(roomId);
        } catch (error) {
            console.error('Error fetching messages:', error);
        }
    };

    // Send admin message via WebSocket
    const sendAdminResponse = async (e) => {
        e.preventDefault();
        if (!socket || !response.trim() || !selectedRoom) return;

        const timestamp = new Date().toISOString();
        const messageData = {
            message: response,
            sender: 'Admin',
            room_id: selectedRoom,
            is_admin_message: true,
            timestamp,
        };

        // Optimistic UI update
        setMessages((prev) => {
            const updated = deduplicateMessages([...prev, messageData]);
            return updated.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        });

        try {
            console.log('📤 Sending WebSocket message:', messageData);
            socket.send(JSON.stringify(messageData));
            setResponse('');
        } catch (error) {
            console.error('❌ Error sending message:', error);
        }
    };

    // Close emoji picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
                setShowEmojiPicker(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Add emoji to the input field
    const addEmoji = (emoji) => {
        setResponse((prev) => prev + emoji.native);
    };

    if (!isAdmin) return null;

    return (
        <div className="min-h-screen pt-20 bg-white dark:bg-gray-900">
            <div className="container mx-auto px-6 md:px-12 lg:px-20">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden">
                    {/* Header */}
                    <div className="p-6 border-b dark:border-gray-700">
                        <h2 className="text-2xl font-extrabold text-gray-800 dark:text-white">
                            Admin Chat Dashboard
                        </h2>
                    </div>

                    {/* Main Layout */}
                    <div className="flex flex-col md:flex-row h-screen">
                        {/* ChatList component */}
                        <ChatList
                            chats={chats}
                            selectedRoom={selectedRoom} // Now it's a string, initially ''
                            onSelectRoom={handleRoomSelect}
                        />

                        {/* Messages area */}
                        <div className="w-2/3 flex flex-col">
                            <div className="flex-1 overflow-y-auto p-6">
                                {messages.map((msg, index) => (
                                    <div
                                        key={`${msg.timestamp}-${index}`}
                                        className={`mb-4 ${msg.sender === 'Admin' ? 'text-right' : ''}`}
                                    >
                                        <div
                                            className={`inline-block p-3 rounded-lg max-w-[80%] ${msg.sender === 'Admin'
                                                ? 'bg-blue-500 text-white'
                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-white'
                                                }`}
                                        >
                                            <p className="text-sm">{msg.message}</p>
                                            <small className="text-xs opacity-75 mt-1 block">{msg.sender}</small>
                                        </div>
                                    </div>
                                ))}
                                <div ref={messagesEndRef} />
                            </div>

                            {selectedRoom && (
                                <form onSubmit={sendAdminResponse} className="p-4 border-t dark:border-gray-700 flex gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                        className="px-3 py-2 bg-gray-300 dark:bg-gray-700 rounded-lg"
                                    >
                                        😀
                                    </button>
                                    {showEmojiPicker && (
                                        <div
                                            ref={emojiPickerRef}
                                            className="absolute bottom-16 right-2 z-50 bg-white rounded-lg shadow-lg"
                                        >
                                            <Picker onEmojiSelect={addEmoji} />
                                        </div>
                                    )}
                                    <input
                                        type="text"
                                        value={response}
                                        onChange={(e) => setResponse(e.target.value)}
                                        className="flex-1 p-3 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                        placeholder="Type your response..."
                                    />
                                    <button
                                        type="submit"
                                        className="px-6 py-3 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600"
                                    >
                                        Send
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminChatInterface;
